import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../cn.js';

export type StepStatus = 'complete' | 'active' | 'pending';

export interface StepperItem {
  /** e.g. "Goal Evaluation" — the step's own name, "Step N" numbering is added by the component. */
  label: string;
  /** e.g. "100% Verified", "(1 of 3 Approved)", "(Scheduled)" — the mockup's per-step trailing
   *  status text, rendered under the label. */
  meta?: ReactNode;
  status: StepStatus;
}

export interface StepperProps {
  steps: StepperItem[];
  /** The dashboard's dot-only rail and mobile's compressed tracker both want the identical
   *  status/ordering logic without the label column — see the Performance Reviews plan's
   *  `ReviewStageTracker.tsx` note ("check if Section 1's Dashboard already built a compact
   *  variant of the identical thing" — this prop *is* that variant, not a second component). */
  compact?: boolean;
  className?: string;
}

const CONNECTOR_TONE: Record<StepStatus, string> = {
  complete: 'bg-accent',
  active: 'bg-border',
  pending: 'bg-border',
};

const DOT_TONE: Record<StepStatus, string> = {
  complete: 'border-accent bg-accent text-fg-on-accent',
  active: 'border-accent bg-surface text-accent',
  pending: 'border-border bg-surface text-fg-subtle',
};

/**
 * A numbered multi-step progress tracker — "Step 1 Goal Evaluation ✓ 100% Verified" / "Step 2
 * Competency Matrix [active] 50% Complete" / ... (see the Performance Reviews plan's Requirements
 * section for the exact mockup copy this renders). Generic over any ordered process with a
 * complete/active/pending status per step — not review-specific; `ReviewStageTracker.tsx` is the
 * thin, domain-aware wrapper that computes `steps` from a `PerformanceReview`.
 *
 * `compact` collapses the label column to just the numbered dots + connector line, for a narrow
 * card or the mobile-width layout — same status contract, no label text, so the two variants never
 * drift out of sync with each other.
 */
export function Stepper({ steps, compact = false, className }: StepperProps) {
  return (
    <ol className={cn('flex w-full items-start', className)} aria-label="Progress">
      {steps.map((step, index) => (
        <li key={step.label} className="flex flex-1 items-start last:flex-none">
          <div className="flex flex-col items-center">
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold',
                DOT_TONE[step.status],
              )}
              aria-current={step.status === 'active' ? 'step' : undefined}
            >
              {step.status === 'complete' ? <Check className="size-3.5" /> : index + 1}
            </span>
            {!compact && (
              <div className="mt-1.5 max-w-[9rem] text-center">
                <p
                  className={cn(
                    'text-xs font-semibold',
                    step.status === 'pending' ? 'text-fg-subtle' : 'text-fg',
                  )}
                >
                  Step {index + 1} {step.label}
                </p>
                {step.meta && <p className="mt-0.5 text-[11px] text-fg-muted">{step.meta}</p>}
              </div>
            )}
          </div>
          {index < steps.length - 1 && (
            <div
              className={cn('mt-3.5 h-0.5 flex-1', CONNECTOR_TONE[step.status])}
              aria-hidden="true"
            />
          )}
        </li>
      ))}
    </ol>
  );
}
